// Reporting's Compare dialog (owner, 2026-11-04 — D-463: "Can Compare be a
// button just left of Download? When clicked, open a modal. Let the user pick
// between comparing different filter conditions or different reports. If the
// former, show filter options; if the latter show report options. Offer Apply
// and Close buttons, as per usual. Once applied, show the side-by-side.").
//
// THE FILTERS DIALOG'S SHELL (`FilterDialog.tsx`): a draft, Apply commits it
// and closes, Close or a click outside discards it. The left-hand button is
// Reset while nothing is being compared, and Stop Comparing once something is.
//
// THE KIND IS A DROPDOWN, "Comparison" (owner, 2026-11-04: "make that a
// dropdown picker under Comparison"); it was a segmented control for a day.
// BOTH LISTS OPEN BELOW THEIR FIELD, inside the dialog, as every picker in a
// pop-up does (`popupPositioning.ts`): the Report list first opened to the side.
import { useEffect, useState } from 'react'
import { Dropdown, Field, Option, OptionGroup, makeStyles } from '@fluentui/react-components'
import { FilterDialog } from './FilterDialog'
import { CriteriaFields } from './DashboardFilterButton'
import { popupPositionProps, usePopupListboxClass } from './popupPositioning'
import { REPORTING_CHARTS, reportingChartById } from '../lib/codeCharts'
import type { Criteria } from '../lib/criteria'

/** What the right-hand side shows: the same report under other filters, or another report under the same filters. */
export type Comparison = { mode: 'filters'; criteria: Criteria } | { mode: 'reports'; chartId: string }

type Mode = Comparison['mode']

const MODE_LABEL: Record<Mode, string> = { filters: 'Different Filters', reports: 'Different Reports' }

const CHART_GROUPS = [...new Set(REPORTING_CHARTS.map((chart) => chart.group))]

/** The report to offer first: the next one in the same section. */
function partnerFor(id: string): string {
  const group = REPORTING_CHARTS.filter((c) => c.group === reportingChartById(id).group)
  const at = group.findIndex((c) => c.id === id)
  if (group.length > 1) return group[(at + 1) % group.length]!.id
  const all = REPORTING_CHARTS.findIndex((c) => c.id === id)
  return REPORTING_CHARTS[(all + 1) % REPORTING_CHARTS.length]!.id
}

const useStyles = makeStyles({
  picker: { width: '100%' },
})

interface Props {
  open: boolean
  onClose: () => void
  /** What is being compared now, or `null`. */
  value: Comparison | null
  /** The report on screen, which the other report must differ from. */
  chartId: string
  /** The filters on screen, which seed the other side's filters. */
  criteria: Criteria
  /** The companies the Company picker offers: the Reporting seats' (D-258). */
  companyIds?: readonly string[]
  /** Commit a comparison, or `null` to stop comparing. */
  onApply: (next: Comparison | null) => void
}

export function CompareDialog({ open, onClose, value, chartId, criteria, companyIds, onApply }: Props) {
  const s = useStyles()
  const listbox = usePopupListboxClass()
  const [mode, setMode] = useState<Mode>('filters')
  const [draftCriteria, setDraftCriteria] = useState<Criteria>(criteria)
  const [draftChart, setDraftChart] = useState<string>(partnerFor(chartId))

  // Each opening starts from what is on screen, so Close genuinely discards.
  useEffect(() => {
    if (!open) return
    setMode(value?.mode ?? 'filters')
    setDraftCriteria(value?.mode === 'filters' ? value.criteria : criteria)
    setDraftChart(value?.mode === 'reports' && value.chartId !== chartId ? value.chartId : partnerFor(chartId))
  }, [open, value, chartId, criteria])

  const stopping = value !== null

  return (
    <FilterDialog
      open={open}
      onClose={onClose}
      wide
      title="Compare"
      resetLabel={stopping ? 'Stop Comparing' : 'Reset'}
      onReset={() => {
        if (stopping) {
          onApply(null)
          onClose()
        } else if (mode === 'filters') {
          setDraftCriteria(criteria)
        } else {
          setDraftChart(partnerFor(chartId))
        }
      }}
      onApply={() =>
        onApply(mode === 'filters' ? { mode: 'filters', criteria: draftCriteria } : { mode: 'reports', chartId: draftChart })
      }
    >
      {(host) => (
        <>
          <Field label="Comparison">
            <Dropdown
              className={s.picker}
              {...popupPositionProps(host)}
              listbox={{ className: listbox }}
              value={MODE_LABEL[mode]}
              selectedOptions={[mode]}
              onOptionSelect={(_, d) => d.optionValue && setMode(d.optionValue as Mode)}
            >
              <Option value="filters">{MODE_LABEL.filters}</Option>
              <Option value="reports">{MODE_LABEL.reports}</Option>
            </Dropdown>
          </Field>
          {mode === 'filters' ? (
            <CriteriaFields host={host} draft={draftCriteria} onChange={setDraftCriteria} companyIds={companyIds} />
          ) : (
            <Field label="Report">
              <Dropdown
                className={s.picker}
                {...popupPositionProps(host)}
                listbox={{ className: listbox }}
                value={reportingChartById(draftChart).title}
                selectedOptions={[draftChart]}
                onOptionSelect={(_, d) => d.optionValue && setDraftChart(d.optionValue)}
              >
                {CHART_GROUPS.map((group) => (
                  <OptionGroup key={group} label={group}>
                    {REPORTING_CHARTS.filter((c) => c.group === group && c.id !== chartId).map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.title}
                      </Option>
                    ))}
                  </OptionGroup>
                ))}
              </Dropdown>
            </Field>
          )}
        </>
      )}
    </FilterDialog>
  )
}
